import { ArrowRight } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DailyBriefViewModel } from '../../../services/dailyBriefService';

interface DailyBriefCardProps {
  brief: DailyBriefViewModel;
  onViewMatches?: () => void;
}

const DailyBriefCard: React.FC<DailyBriefCardProps> = ({ brief, onViewMatches }) => {
  const { t } = useTranslation();

  const rows: Array<{ emoji: string; text: React.ReactNode }> = [];

  if (brief.newMatchCount > 0) {
    rows.push({
      emoji: '🔥',
      text: t('dashboard.dailyBrief.newMatches', {
        defaultValue: '{{count}} new jobs match your profile',
        count: brief.newMatchCount,
      }),
    });
  }

  if (brief.strongMatchCount > 0) {
    rows.push({
      emoji: '🎯',
      text: t('dashboard.dailyBrief.strongMatches', {
        defaultValue: '{{count}} are >90% matches',
        count: brief.strongMatchCount,
      }),
    });
  }

  if (brief.highestSalaryLabel) {
    rows.push({
      emoji: '💰',
      text: t('dashboard.dailyBrief.highestSalary', {
        defaultValue: 'Highest salary: {{amount}}',
        amount: brief.highestSalaryLabel,
      }),
    });
  }

  if (brief.followUpsDue > 0) {
    rows.push({
      emoji: '⏰',
      text: t('dashboard.dailyBrief.followUps', {
        defaultValue: '{{count}} applications need follow-up',
        count: brief.followUpsDue,
      }),
    });
  }

  if (brief.interviewsTomorrow > 0) {
    rows.push({
      emoji: '🎤',
      text:
        brief.interviewsTomorrow === 1
          ? brief.interviewTitle
            ? t('dashboard.dailyBrief.interviewTomorrowNamed', {
                defaultValue: 'You have an interview tomorrow — {{title}}',
                title: brief.interviewTitle,
              })
            : t('dashboard.dailyBrief.interviewTomorrow', 'You have an interview tomorrow')
          : t('dashboard.dailyBrief.interviewsTomorrow', {
              defaultValue: '{{count}} interviews tomorrow',
              count: brief.interviewsTomorrow,
            }),
    });
  }

  if (rows.length === 0) {
    rows.push({
      emoji: '✨',
      text: t(
        'dashboard.dailyBrief.empty',
        'Complete your profile and check back for personalized job matches.',
      ),
    });
  }

  return (
    <section className="ds-daily-brief" aria-labelledby="daily-brief-title">
      <div className="ds-daily-brief__header">
        <p className="ds-daily-brief__eyebrow">
          {t('dashboard.dailyBrief.eyebrow', 'Your GoGrabJob Daily Brief')}
        </p>
        <h2 id="daily-brief-title" className="ds-daily-brief__title">
          {t('dashboard.dailyBrief.title', "Here's your day")}
        </h2>
      </div>

      <ul className="ds-daily-brief__list">
        {rows.map((row) => (
          <li key={row.emoji + String(row.text)} className="ds-daily-brief__item">
            <span className="ds-daily-brief__emoji" aria-hidden>{row.emoji}</span>
            <span className="ds-daily-brief__text">{row.text}</span>
          </li>
        ))}
      </ul>

      {onViewMatches ? (
        <button type="button" className="ds-daily-brief__cta" onClick={onViewMatches}>
          {t('dashboard.dailyBrief.cta', 'View my job matches')}
          <ArrowRight className="w-4 h-4" aria-hidden />
        </button>
      ) : null}
    </section>
  );
};

export default React.memo(DailyBriefCard);
